// Zod schemas for Story validation
import { z } from 'zod'
import { Chapter, Step, Story } from '.'

const nullish = <T extends z.ZodTypeAny>(schema: T) => z.union([schema, z.null(), z.undefined()])

export const LocationSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
})

export const StoryLocationSchema = LocationSchema.extend({
  zoom: z.number(),
  pitch: nullish(z.number()),
  bearing: nullish(z.number()),
})

export const RiskMapSlideSchema = z.object({
  src: z.string(),
  type: z.enum(['video', 'image']),
  videoType: nullish(z.string()),
  poster: nullish(z.string()),
  caption: nullish(z.string()),
  attribution: nullish(z.string()),
  dateTaken: nullish(z.string()),
  description: nullish(z.string()),
})

export const StoryMediaSchema = z.object({
  location: LocationSchema,
  activate: nullish(z.boolean()),
  slides: z.array(RiskMapSlideSchema),
})

export const StoryMapStateSchema = z.object({
  elements: nullish(z.array(z.string())),
  scenarios: nullish(z.array(z.string())),
  contexts: nullish(z.array(z.string())),
  basemap: nullish(z.string()),
  regions: nullish(z.array(z.string())),
  location: nullish(StoryLocationSchema),
  media: nullish(z.array(StoryMediaSchema)),
})

export const BrandingSchema = z.object({
  primary: z.string(),
  primaryContrast: z.string(),
  secondary: z.string(),
  secondaryContrast: z.string(),
})

// Section, Step, Chapter and Story schemas are defined separately to allow for recursive nesting of chapters and steps, while still validating the shared StoryMapState properties at each level.

export const StepSectionSchema = z.object({
  header: nullish(z.string()),
  content: nullish(z.string()),
  wideImage: nullish(z.string()),
  sideImage: nullish(z.string()),
  videoUrl: nullish(z.string()),
  buttons: nullish(
    z.array(
      z.object({
        url: z.string(),
        title: z.string(),
      }),
    ),
  ),
})

export const StepSchema = z.object({
  title: z.string(),
  sections: z.array(z.any()), // StepSectionSchema
  ...StoryMapStateSchema.shape,
})

export const ChapterSchema = z.object({
  title: nullish(z.string()),
  description: nullish(z.string()),
  steps: z.array(z.any()), // StepSchema
  ...StoryMapStateSchema.shape,
})

export const StorySchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string(),
  visibility: z.string(),
  coverImageUrl: nullish(z.string()),
  chapters: z.array(z.any()), // ChapterSchema
  branding: nullish(BrandingSchema),
  summary: nullish(z.string()),
  ...StoryMapStateSchema.shape,
})

// The below functions attempt to trim out invalid data while preserving as much of the valid data as possible, with console warnings for any issues encountered.

/**
 * Recursively formats Zod errors into a readable array of strings.
 * Handles nested union errors and outputs each issue as a bullet point.
 * @param error - ZodError or ZodIssue(s) to format
 * @returns Array of formatted error strings
 */
const formatZodError = (
  error: z.ZodError | z.core.$ZodIssue | z.core.$ZodIssue[] | z.core.$ZodIssue[][],
): string[] => {
  if (error instanceof z.ZodError) {
    return error.issues
      .map((issue) => {
        const path = issue.path.length > 0 ? `"${issue.path.join('.')}": ` : ''
        if (issue.code == 'invalid_union') {
          const unionErrors = formatZodError(issue.errors)
          return [`\t\t  • ${path}Invalid union:`, ...unionErrors]
        }
        return [`\t\t  • ${path}${issue.message}`]
      })
      .flat()
  } else if (Array.isArray(error)) {
    return error.map((e) => formatZodError(e).map((e) => '\t' + e)).flat()
  } else {
    const path = error.path.length > 0 ? `"${error.path.join('.')}": ` : ''
    return [`\t\t  • ${path}${error.message}`]
  }
}

/**
 * Validates an array of unknown items against a Zod schema.
 * Returns only valid items and collects formatted errors for invalid entries.
 * @param arr - Array of unknown items to validate
 * @param schema - Zod schema to validate against
 * @param debugName - Name for error context
 * @returns Object with valid results and error messages
 */
const trimArrayToValid = (arr: unknown[], schema: z.ZodTypeAny, debugName: string) => {
  const errors: string[] = []
  const result = arr
    .map((item, i) => {
      const result = schema.safeParse(item)
      if (result.success) {
        return result.data
      } else {
        errors.push(
          `Index ${i} of "${debugName}" array invalid:\n${formatZodError(result.error).join('\n')}`,
        )
        return null
      }
    })
    .filter((item): item is z.infer<typeof schema> => item !== null)
  return { result, errors }
}

/**
 * Sanitizes an array of chapters, validating each chapter, step, and section.
 * Returns valid chapters and collects all errors encountered during validation.
 * @param chapters - Array of unknown chapter objects
 * @returns Object with valid chapters and error messages
 */
const sanitizeChapters = (chapters: unknown[]) => {
  const errors: string[] = []
  const { result: chapterResults, errors: chapterErrors } = trimArrayToValid(
    chapters,
    ChapterSchema,
    'chapters',
  )
  errors.push(...chapterErrors)
  const chaptersResult = chapterResults
    .map((chapter: unknown) => {
      const typedChapter = chapter as Chapter
      if (!Array.isArray(typedChapter.steps)) {
        return null
      }
      const { result: stepResults, errors: stepErrors } = trimArrayToValid(
        typedChapter.steps,
        StepSchema,
        'steps',
      )
      if (stepErrors.length > 0) {
        errors.push(
          `Chapter "${typedChapter.title}" has invalid steps:\n${stepErrors.map((e) => `\t  • ${e}`).join('\n')}`,
        )
      }
      const steps = stepResults
        .map((step: unknown) => {
          const typedStep = step as Step
          if (!Array.isArray(typedStep.sections)) {
            errors.push(`No sections array found for step "${typedStep.title}".`)
            return null
          }
          const { result: sectionResults, errors: sectionErrors } = trimArrayToValid(
            typedStep.sections,
            StepSectionSchema,
            'sections',
          )
          errors.push(...sectionErrors)
          return {
            ...typedStep,
            sections: sectionResults,
          }
        })
        .filter(Boolean)
      if (steps.length === 0) {
        errors.push(`No valid steps found for chapter "${typedChapter.title}".`)
        return null
      }
      return {
        ...typedChapter,
        steps,
      }
    })
    .filter(Boolean)
  return {
    chapters: chaptersResult,
    errors,
  }
}

/**
 * Sanitizes an array of stories, validating each story, chapter, step, and section.
 * Returns valid stories and logs all errors encountered during validation.
 * @param stories - Array of unknown story objects
 * @returns Array of valid stories
 */
export const sanitizeStories = (stories: unknown[]) => {
  const { result: storyResults, errors: storyErrorsFromTrim } = trimArrayToValid(
    stories,
    StorySchema,
    'stories',
  )
  if (storyResults.length === 0) {
    // eslint-disable-next-line no-console
    console.warn('No valid stories found after sanitization.')
    if (storyErrorsFromTrim.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'Story validation errors:',
        storyErrorsFromTrim.map((e) => `  • ${e}`).join('\n'),
      )
    }
  } else {
    storyResults.forEach((storyRaw, i) => {
      const story = storyRaw as Story
      let storyErrors: string[] = []
      if (!Array.isArray(story.chapters)) {
        storyErrors.push(`No chapters array found for story "${story.title}".`)
      } else {
        const { chapters, errors } = sanitizeChapters(story.chapters)
        if (chapters.length === 0) {
          storyErrors.push(`No valid chapters found for story "${story.title}".`)
        }
        if (errors.length > 0) {
          storyErrors = storyErrors.concat(errors)
        }
        if (chapters.length > 0) {
          storyResults[i] = {
            ...story,
            chapters,
          }
        }
      }
      if (storyErrors.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `Story "${story.title}" is invalid:\n`,
          storyErrors.map((e) => `  • ${e}`).join('\n'),
        )
      }
    })
    if (storyErrorsFromTrim.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'Story validation errors:',
        storyErrorsFromTrim.map((e) => `  • ${e}`).join('\n'),
      )
    }
  }
  return storyResults as Story[]
}
